import React, { useState } from 'react';
import { createEvent } from '../services/eventsService';
import { getDisplayErrorMessage } from '../services/apiError';
import { useParams, useNavigate } from 'react-router-dom';
import CloseButton from './CloseButton';
import RichTextEditor from './RichTextEditor';
import ErrorMessage from './ErrorMessage';
import { MAX_IMAGE_SIZE_LABEL, validateImageSize, isRealDate } from './constants';
import DateInput from './DateInput';

const CreateEventForm = () => {
  const [name, setName] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [fullDescription, setFullDescription] = useState('');
  const { historyId } = useParams();
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [image, setImage] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [isHoveringButton, setIsHoveringButton] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({
    name: '',
    shortDescription: '',
    fullDescription: '',
    startDate: '',
    endDate: '',
    image: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    
    setError(null);
    setErrors({});
    setSuccessMessage(null);

    const validationErrors = {};

    if (name.trim() === "") {
      validationErrors.name = "Name is required";
    }

    const startDateTimeStr = `${startDate}T${startTime || '00:00:00'}Z`;
    const startDateObj = new Date(startDateTimeStr);

    if (startDate.trim() !== "" && (isNaN(startDateObj.getTime()) || !isRealDate(startDate))) {
      validationErrors.startDate = "Invalid start date";
    }

    if (endDate.trim() !== "") {
      const endDateTimeStr = `${endDate}T${endTime || '00:00:00'}Z`;
      const endDateObj = new Date(endDateTimeStr);
      if (isNaN(endDateObj.getTime()) || !isRealDate(endDate)) {
        validationErrors.endDate = "Invalid end date";
      } else if (!isNaN(startDateObj.getTime()) && startDateObj.getTime() > endDateObj.getTime()) {
        validationErrors.endDate = "End date must be later than start date";
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const startDateTs = Math.floor(startDateObj.getTime() / 1000);
      
      let endDateTs = null;
      if (endDate.trim() !== '') {
        const endDateTimeStr = `${endDate}T${endTime || '00:00:00'}Z`;
        endDateTs = Math.floor(new Date(endDateTimeStr).getTime() / 1000);
      }
      const event = {
        name,
        shortDescription: shortDescription,
        fullDescription: fullDescription,
        startDateTs,
        endDateTs
      }
      const response = await createEvent(event, image, historyId);
      setSuccessMessage("Event successfully created!");
      console.log(response);

      setName('');
      setShortDescription('');
      setFullDescription('');
      setStartDate('');
      setStartTime('');
      setEndDate('');
      setEndTime('');
      setImage(null);
      setImagePreview(null);
      setFormKey(prev => prev + 1);
    } catch (err) {
      setError(getDisplayErrorMessage(err));
    }

    setIsLoading(false);
  };
  
  const validateField = (name, value) => {
    let error = "";
    
    switch (name) {
      case "name":
        if (value.trim() === "") {
          error = "Name is required";
        }
        break;
      case "startDate":
        if (value.trim() !== "" && !isRealDate(value)) {
          error = "Invalid date";
        }
        break;
      case "endDate":
        if (value.trim() !== '' && !isRealDate(value)) {
          error = "Invalid date";
        } else if (startDate.trim() !== '' && value.trim() !== '' && isRealDate(startDate) && isRealDate(value)) {
          const sTime = new Date(`${startDate}T${startTime || '00:00:00'}Z`).getTime();
          const eTime = new Date(`${value}T${endTime || '00:00:00'}Z`).getTime();
          if (sTime > eTime) {
            error = "End date must be after start date";
          }
        }
        break;
      default:
        break;
      }

    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }

    if (file) {
      if (validateImageSize(file, setErrors, setImage, setImagePreview)) return;
      setImage(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    } else {
      setImage(null);
      setImagePreview(null);
    }
  };

  const handlePaste = (e) => {
    if (!isHoveringButton) {
      return;
    }
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();

        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
        }

        if (file) {
          if (validateImageSize(file, setErrors, setImage, setImagePreview)) {
            e.preventDefault();
            break;
          }
          setImage(file);
          const previewUrl = URL.createObjectURL(file);
          setImagePreview(previewUrl);
        }
        e.preventDefault();
        break;
      }
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const removeImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImage(null);
    setImagePreview(null);
    setErrors(prev => ({ ...prev, image: '' }));
  };

  const handleDragLeave = (e) => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;

    if (files && files.length > 0) {
      const file = files[0];

      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }

      if (file) {
        if (file.type.startsWith('image/')) {
          if (validateImageSize(file, setErrors, setImage, setImagePreview)) return;
          setImage(file);
          const previewUrl = URL.createObjectURL(file);
          setImagePreview(previewUrl);
        } else {
          setErrors(prev => ({ ...prev, image: 'Only image files are allowed' }));
        }
      }
    }
  }

  const isFormValid = name.trim() !== '' && startDate.trim() !== '' && isRealDate(startDate);

  return (
    <div className="h-[calc(100vh-64px)] overflow-y-auto flex items-start justify-center bg-gray-100 py-8">
      <div className="bg-gray-200 mx-auto w-full max-w-4xl rounded shadow-md py-6 relative" onClick={() => setSuccessMessage(null)}>
      <ErrorMessage message={error} onDismiss={() => setError(null)} />
      {successMessage && (
        <div 
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-green-100 text-green-500 text-center shadow-2xl px-6 py-3 border-green-400 border-[1px] rounded-xl hover:cursor-pointer z-50"
          onClick={ () => setSuccessMessage(null)}
        >
        {successMessage}
        </div>
      )}

        <div className="flex px-4 justify-end">
          <CloseButton onClick={() => navigate(-1)} disabled={isLoading}/>
        </div>

        <form
          key={ formKey } 
          className="bg-gray-300 rounded px-32 pt-6 pb-8 my-4 flex flex-col shadow-lg" onSubmit={ handleSubmit } onPaste={handlePaste}
        >
          <h1 className="text-center text-3xl mb-6 bg-gray-200 border-2 border-gray-400 rounded-xl py-3">Create event for history</h1>
          <div className="mb-6 relative">
          <span className="absolute -left-5 top-1 text-xl text-red-500 font-bold">*</span>
          <input 
            placeholder="Enter a name for event" 
            type="text" 
            className="w-full rounded p-1" 
            value={ name } 
            onChange={ (e) => setName(e.target.value) }
            onBlur={ (e) => validateField('name', e.target.value)}
          />
          {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
          </div>

          <div className="mb-6">
          <input 
            placeholder="Enter a short description of the event" 
            type="text" 
            className="w-full rounded p-1" 
            value={ shortDescription } 
            onChange={ (e) => setShortDescription(e.target.value) }
          />
          {errors.shortDescription && <p className="text-red-500 text-xs">{errors.shortDescription}</p>}
          </div>

          <div className="mb-4">
            <RichTextEditor
              content={fullDescription}
              onChange={setFullDescription}
              placeholder="Enter a full description of the event"
            />
            {errors.fullDescription && <p className="text-red-500 text-xs">{errors.fullDescription}</p>}
          </div>

          <div className="mb-6 relative">
            <span className="absolute -left-5 top-1 text-xl text-red-500 font-bold">*</span>
            <div className="flex gap-2">
              <DateInput
                className="flex-1"
                value={startDate}
                onChange={(val) => {
                  const wasEmpty = !startDate;
                  setStartDate(val);
                  if (val && wasEmpty && !startTime) {
                    setStartTime('00:00:00');
                  }
                }}
                onBlur={(val) => validateField('startDate', val)}
              />
              <button
                type="button"
                className="bg-gray-200 rounded p-1 hover:bg-blue-100 hover:border-blue-500 border-2 text-sm shrink-0"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setStartDate(today);
                  setErrors(prev => ({ ...prev, startDate: '' }));
                  if (!startTime) {
                    setStartTime('00:00:00');
                  }
                }}
              >Today</button>
              <input
                type="time"
                step="1"
                className="rounded w-32 p-1 shrink-0"
                value={ startTime }
                onChange={ (e) => setStartTime(e.target.value) }
              />
              <button
                type="button"
                className="bg-gray-200 rounded p-1 hover:bg-blue-100 hover:border-blue-500 border-2 text-sm shrink-0"
                onClick={() => setStartTime(new Date().toTimeString().split(' ')[0])}
              >Now</button>
            </div>
            {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>}
          </div>

          <div className="mb-6">
            <div className="flex gap-2">
              <DateInput
                className="flex-1"
                value={endDate}
                onChange={(val) => {
                  const wasEmpty = !endDate;
                  setEndDate(val);
                  if (val && wasEmpty && !endTime) {
                    setEndTime('00:00:00');
                  }
                }}
                onBlur={(val) => validateField('endDate', val)}
              />
              <button
                type="button"
                className="bg-gray-200 rounded p-1 hover:bg-blue-100 hover:border-blue-500 border-2 text-sm shrink-0"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setEndDate(today);
                  setErrors(prev => ({ ...prev, endDate: '' }));
                  if (!endTime) {
                    setEndTime('00:00:00');
                  }
                }}
              >Today</button>
              <input
                type="time"
                step="1"
                className="rounded w-32 p-1 shrink-0"
                value={ endTime }
                onChange={ (e) => setEndTime(e.target.value) }
              />
              <button
                type="button"
                className="bg-gray-200 rounded p-1 hover:bg-blue-100 hover:border-blue-500 border-2 text-sm shrink-0"
                onClick={() => setEndTime(new Date().toTimeString().split(' ')[0])}
              >Now</button>
            </div>
            {errors.endDate && <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>}
          </div>

          <div className="mb-6 flex items-center gap-4">
            <label 
              htmlFor="image-upload" 
              className={`${isDragging ? 'border-dashed border-blue-500 bg-blue-200' : ''} flex-1 text-center cursor-pointer bg-gray-200 rounded p-2 hover:bg-blue-100 hover:border-blue-500 border-2 disabled:opacity-50 disabled:cursor-not-allowed text400`}
              onMouseEnter={() => setIsHoveringButton(true)}
              onMouseLeave={() => setIsHoveringButton(false)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              Click to upload / Paste from clipboard / Drop image here
              {errors.image
                ? <span className="block text-xs text-red-500 mt-1">{errors.image}</span>
                : <span className="block text-xs text-gray-500 mt-1">Max size: {MAX_IMAGE_SIZE_LABEL}</span>
              }
            </label>
            <input
              id="image-upload"
              type="file" 
              onChange={handleImageChange}
              className="hidden"
              accept="image/*"
            />


            {imagePreview && (
              <div className="w-16 h-16 flex-shrink-0 border-2 border-gray-400 rounded bg-white relative">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-full h-full object-cover" 
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center leading-none hover:bg-red-600 p-0"
                >
                  <svg viewBox="0 0 10 10" className="w-3 h-3" stroke="currentColor" strokeWidth="1.5" fill="none"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>
                </button>
              </div>
            )}
          </div>

          <button
            disabled={ isLoading || !isFormValid }
            type="submit" className="bg-gray-200 rounded p-2 hover:bg-blue-100 hover:border-blue-500 border-2 400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-200 disabled:hover:border-gray-200"
          >
          {isLoading ? 'Processing...' : 'Create event'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default CreateEventForm;